import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Job } from "@/pages/admin/jobs/components/JobModal";

type JobNote = {
  id: string;
  tenant_id: string;
  job_id: string;
  user_id: string | null;
  note: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
};

interface Props {
  job: Job;
  tenantId: string;
  onNoteCountChange?: (count: number) => void;
}

const MAX_LENGTH = 1000;

function fmtDateTime(d: string) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function NotesTab({ job, tenantId, onNoteCountChange }: Props) {
  const [notes, setNotes] = useState<JobNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [pinningId, setPinningId] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    const { data, error } = await supabase
      .from("job_notes")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("is_pinned", { ascending: false })
      .order("created_at", { ascending: false });
    if (!error) {
      const list = (data as JobNote[]) ?? [];
      setNotes(list);
      onNoteCountChange?.(list.length);
    }
    setLoading(false);
  }, [job.id, tenantId, onNoteCountChange]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("job_notes")
      .insert({
        tenant_id: tenantId,
        job_id: job.id,
        user_id: userData?.user?.id ?? null,
        note: text,
        is_pinned: false,
      })
      .select("*")
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      const updated = [data as JobNote, ...notes];
      setNotes(updated);
      setDraft("");
      onNoteCountChange?.(updated.length);
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from("job_notes").delete().eq("id", id).eq("tenant_id", tenantId);
    if (!error) {
      const updated = notes.filter((n) => n.id !== id);
      setNotes(updated);
      onNoteCountChange?.(updated.length);
    }
    setDeletingId(null);
  };

  const handleTogglePin = async (note: JobNote) => {
    setPinningId(note.id);
    const { data } = await supabase
      .from("job_notes")
      .update({ is_pinned: !note.is_pinned, updated_at: new Date().toISOString() })
      .eq("id", note.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (data) {
      const updated = notes
        .map((n) => n.id === note.id ? (data as JobNote) : n)
        .sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
      setNotes(updated);
    }
    setPinningId(null);
  };

  const startEdit = (note: JobNote) => {
    setEditingId(note.id);
    setEditDraft(note.note);
  };

  const saveEdit = async (id: string) => {
    const text = editDraft.trim();
    if (!text) return;
    const { error } = await supabase
      .from("job_notes")
      .update({ note: text, updated_at: new Date().toISOString() })
      .eq("id", id)
      .eq("tenant_id", tenantId);
    if (!error) setNotes((prev) => prev.map((n) => n.id === id ? { ...n, note: text } : n));
    setEditingId(null);
    setEditDraft("");
  };

  const onKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) action();
    if (e.key === "Escape") { setEditingId(null); setEditDraft(""); }
  };

  const charLeft = MAX_LENGTH - draft.length;
  const editCharLeft = MAX_LENGTH - editDraft.length;
  const pinnedNotes = notes.filter((n) => n.is_pinned);
  const unpinnedNotes = notes.filter((n) => !n.is_pinned);

  return (
    <div className="space-y-4">
      {/* Compose box */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
            <i className="ri-sticky-note-2-line text-[#C8A96E]"></i>
            New Internal Note
          </h3>
          {notes.length > 0 && (
            <span className="text-slate-300 text-xs">{notes.length} note{notes.length !== 1 ? "s" : ""}</span>
          )}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => onKeyDown(e, handleAdd)}
          rows={3}
          maxLength={MAX_LENGTH}
          placeholder="Add a note — call made, client feedback, follow-up plan, shot list, vendor contacts..."
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all resize-none"
        />
        <div className="flex items-center justify-between mt-2.5">
          <span className={`text-[10px] font-mono ${charLeft < 100 ? "text-amber-500" : "text-slate-300"}`}>
            {charLeft} chars left · ⌘Enter to save
          </span>
          <button
            onClick={handleAdd}
            disabled={saving || !draft.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-black text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving
              ? <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              : <i className="ri-add-line text-sm"></i>
            }
            Add Note
          </button>
        </div>
      </div>

      {/* Notes list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-200 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-sticky-note-2-line text-slate-300 text-xl"></i>
          </div>
          <p className="text-slate-500 text-sm font-medium">No notes yet</p>
          <p className="text-slate-400 text-xs max-w-xs leading-relaxed">
            Keep track of vendor contacts, shot lists, client requests and any internal details.
          </p>
        </div>
      ) : (
        <>
          {/* Pinned */}
          {pinnedNotes.length > 0 && (
            <div className="space-y-2">
              <p className="text-slate-400 text-xs font-medium flex items-center gap-1.5 uppercase tracking-wider px-1">
                <i className="ri-pushpin-fill text-[#C8A96E] text-xs"></i>
                Pinned
              </p>
              {pinnedNotes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  editingId={editingId}
                  editDraft={editDraft}
                  editCharLeft={editCharLeft}
                  deletingId={deletingId}
                  pinningId={pinningId}
                  onStartEdit={startEdit}
                  onSaveEdit={saveEdit}
                  onCancelEdit={() => { setEditingId(null); setEditDraft(""); }}
                  onEditDraftChange={setEditDraft}
                  onDelete={handleDelete}
                  onTogglePin={handleTogglePin}
                  onKeyDown={onKeyDown}
                  pinned
                />
              ))}
            </div>
          )}

          {/* Regular */}
          {unpinnedNotes.length > 0 && (
            <div className="space-y-2">
              {pinnedNotes.length > 0 && (
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wider px-1">Notes</p>
              )}
              {unpinnedNotes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  editingId={editingId}
                  editDraft={editDraft}
                  editCharLeft={editCharLeft}
                  deletingId={deletingId}
                  pinningId={pinningId}
                  onStartEdit={startEdit}
                  onSaveEdit={saveEdit}
                  onCancelEdit={() => { setEditingId(null); setEditDraft(""); }}
                  onEditDraftChange={setEditDraft}
                  onDelete={handleDelete}
                  onTogglePin={handleTogglePin}
                  onKeyDown={onKeyDown}
                  pinned={false}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Sub-component: NoteCard ── */
interface NoteCardProps {
  note: JobNote;
  editingId: string | null;
  editDraft: string;
  editCharLeft: number;
  deletingId: string | null;
  pinningId: string | null;
  pinned: boolean;
  onStartEdit: (n: JobNote) => void;
  onSaveEdit: (id: string) => void;
  onCancelEdit: () => void;
  onEditDraftChange: (v: string) => void;
  onDelete: (id: string) => void;
  onTogglePin: (n: JobNote) => void;
  onKeyDown: (e: React.KeyboardEvent, action: () => void) => void;
}

function NoteCard({
  note, editingId, editDraft, editCharLeft, deletingId, pinningId, pinned,
  onStartEdit, onSaveEdit, onCancelEdit, onEditDraftChange, onDelete, onTogglePin, onKeyDown,
}: NoteCardProps) {
  return (
    <div className={`bg-white rounded-xl border transition-all group hover:border-slate-300 ${
      pinned ? "border-[#C8A96E]/30 bg-[#C8A96E]/2" : "border-slate-200"
    }`}>
      {editingId === note.id ? (
        <div className="p-4 space-y-2.5">
          <textarea
            value={editDraft}
            onChange={(e) => onEditDraftChange(e.target.value)}
            onKeyDown={(e) => onKeyDown(e, () => onSaveEdit(note.id))}
            rows={3}
            maxLength={1000}
            autoFocus
            className="w-full bg-slate-50 border border-[#C8A96E]/50 rounded-lg px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#C8A96E] transition-all resize-none"
          />
          <div className="flex items-center justify-between">
            <span className={`text-[10px] font-mono ${editCharLeft < 100 ? "text-amber-500" : "text-slate-300"}`}>
              {editCharLeft} chars left
            </span>
            <div className="flex items-center gap-2">
              <button onClick={onCancelEdit} className="px-3 py-1.5 text-xs text-slate-400 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button onClick={() => onSaveEdit(note.id)} disabled={!editDraft.trim()} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-black text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-save-line text-xs"></i> Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 p-4">
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${pinned ? "bg-[#C8A96E]/15" : "bg-slate-100"}`}>
            <i className={`${pinned ? "ri-pushpin-fill text-[#C8A96E]" : "ri-sticky-note-2-line text-slate-400"} text-xs`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap break-words">{note.note}</p>
            <p className="text-slate-300 text-[10px] mt-2 flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {fmtDateTime(note.created_at)}
              {note.updated_at !== note.created_at && (
                <span className="text-slate-200"> · edited</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onTogglePin(note)}
              disabled={pinningId === note.id}
              title={pinned ? "Unpin" : "Pin note"}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                pinned ? "text-[#C8A96E] hover:bg-[#C8A96E]/10" : "text-slate-300 hover:text-[#C8A96E] hover:bg-[#C8A96E]/10"
              }`}
            >
              <i className="ri-pushpin-line text-xs"></i>
            </button>
            <button onClick={() => onStartEdit(note)} title="Edit" className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer">
              <i className="ri-edit-2-line text-xs"></i>
            </button>
            <button
              onClick={() => onDelete(note.id)}
              disabled={deletingId === note.id}
              title="Delete"
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition-all cursor-pointer"
            >
              {deletingId === note.id
                ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                : <i className="ri-delete-bin-6-line text-xs"></i>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
}